import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API_BASE = `${BACKEND_URL}/api`;

const api = axios.create({
  baseURL: API_BASE,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Request interceptor to add auth token
api.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('auth_token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => Promise.reject(error)
);

// Response interceptor for error handling
api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      localStorage.removeItem('auth_token');
      localStorage.removeItem('user');
      window.location.href = '/login';
    }
    return Promise.reject(error);
  }
);

// Auth API
export const authAPI = {
  login: (email, password) => api.post('/auth/login', { email, password }),
  register: (data) => api.post('/auth/register', data),
  getMe: () => api.get('/auth/me'),
  requestAccess: (data) => api.post('/auth/request-access', data),
  verifyToken: (token) => api.get(`/auth/verify-token?token=${token}`),
  activate: (token, password) => api.post('/auth/activate', { token, password }),
};

// Admin API
export const adminAPI = {
  getAuthorizedEmails: () => api.get('/admin/authorized-emails'),
  addAuthorizedEmail: (data) => api.post('/admin/authorized-emails', data),
  removeAuthorizedEmail: (id) => api.delete(`/admin/authorized-emails/${id}`),
  getUsers: () => api.get('/admin/users'),
  toggleUserActive: (userId) => api.put(`/admin/users/${userId}/toggle-active`),
  importTournaments: (tournaments) => api.post('/admin/import-tournaments', tournaments),
  triggerScrape: () => api.post('/admin/scrape-tournaments'),
  getScrapeStatus: () => api.get('/admin/scrape-status'),
  getAccessRequests: (status) => api.get('/admin/access-requests', { params: status ? { status } : {} }),
  approveAccessRequest: (id) => api.post(`/admin/access-requests/${id}/approve`),
  rejectAccessRequest: (id) => api.post(`/admin/access-requests/${id}/reject`),
  resendActivation: (userId) => api.post(`/admin/resend-activation/${userId}`),
  resetActivation: (userId) => api.post(`/admin/reset-activation/${userId}`),
  
  // Email composer
  composeEmail: (data) => api.post('/admin/compose-email', data),
  getEmailLogs: (limit = 50, status = null) => api.get('/admin/email-logs', { params: { limit, status } }),
  getEmailLogDetail: (id) => api.get(`/admin/email-logs/${id}`),
  getUserEmailsForCompose: () => api.get('/admin/user-emails'),
};

// Organizations API
export const organizationsAPI = {
  getAll: (familyArcherId) => api.get('/organizations', { params: familyArcherId ? { family_archer_id: familyArcherId } : {} }),
  link: (data) => api.post('/organizations', data),
  update: (id, data) => api.put(`/organizations/${id}`, data),
  delete: (id) => api.delete(`/organizations/${id}`),
};

// Tournaments API
export const tournamentsAPI = {
  getAll: (params) => api.get('/tournaments', { params }),
  getById: (id) => api.get(`/tournaments/${id}`),
  create: (data) => api.post('/tournaments', data),
  getLastUpdated: () => api.get('/tournaments/last-updated'),
};

// My Tournaments API (tracking)
export const myTournamentsAPI = {
  getAll: (archerId) => api.get('/my-tournaments', { params: archerId ? { archer_id: archerId } : {} }),
  track: (data) => api.post('/my-tournaments', data),
  update: (trackingId, data) => api.put(`/my-tournaments/${trackingId}`, data),
  untrack: (tournamentId, archerId) => api.delete(`/my-tournaments/${tournamentId}`, { 
    params: archerId ? { archer_id: archerId } : {} 
  }),
};

// Family Archers API (for parent accounts)
export const familyArchersAPI = {
  getAll: () => api.get('/family-archers'),
  getById: (id) => api.get(`/family-archers/${id}`),
  add: (data) => api.post('/family-archers', data),
  update: (id, data) => api.put(`/family-archers/${id}`, data),
  delete: (id) => api.delete(`/family-archers/${id}`),
};

// Results API
export const resultsAPI = {
  getAll: () => api.get('/results'),
  add: (data) => api.post('/results', data),
  delete: (id) => api.delete(`/results/${id}`),
  bulkImport: (results) => api.post('/results/bulk-import', { results }),
  importCSV: (csvContent) => api.post('/results/import-csv', { csv_content: csvContent }),
  importPDF: (pdfBase64, filename) => api.post('/results/import-pdf', { pdf_base64: pdfBase64, filename }),
  importURL: (url, archerNames, tournamentName, eventDate) => api.post('/results/import-url', { 
    url, 
    archer_names: archerNames,
    tournament_name: tournamentName,
    event_date: eventDate
  }),
  previewURL: (url) => api.get('/results/preview-url', { params: { url } }),
  getMyArchers: () => api.get('/results/my-archers'),
  getCSVTemplate: () => api.get('/results/csv-template'),
};

// Export API
export const exportAPI = {
  getSchedulePDF: (params = {}) => api.get('/export/schedule/pdf', { 
    params,
    responseType: 'blob'
  }),
  getCalendarPDF: (params = {}) => api.get('/export/calendar/pdf', { 
    params,
    responseType: 'blob'
  }),
};

// Notifications API
export const notificationsAPI = {
  getAll: (unreadOnly = false) => api.get('/notifications', { params: { unread_only: unreadOnly } }),
  markRead: (id) => api.put(`/notifications/${id}/read`),
  markAllRead: () => api.put('/notifications/mark-all-read'),
  getPreferences: () => api.get('/notifications/preferences'),
  updatePreferences: (prefs) => api.put('/notifications/preferences', prefs),
};

// Analytics API
export const analyticsAPI = {
  analyze: (query, includeResults = true, includeTournaments = true) =>
    api.post('/analytics/analyze', { query, include_results: includeResults, include_tournaments: includeTournaments }),
  getUsage: () => api.get('/analytics/usage'),
};

// Profile API
export const profileAPI = {
  get: () => api.get('/profile'),
  update: (data) => api.put('/profile', data),
};

// Club API (for coaches)
export const clubAPI = {
  getMembers: () => api.get('/club/members'),
  getEvents: () => api.get('/club/events'),
};

// Dashboard API
export const dashboardAPI = {
  getSummary: () => api.get('/dashboard/summary'),
};

// Email API
export const emailAPI = {
  sendNotification: (data) => api.post('/send-notification-email', data),
};

// Feedback API
export const feedbackAPI = {
  submit: (data) => api.post('/feedback/submit', data),
};

// Line Assignments API
export const lineAssignmentsAPI = {
  uploadPDF: (tournamentId, file) => {
    const formData = new FormData();
    formData.append('file', file);
    return api.post(`/line-assignments/upload/${tournamentId}`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' }
    });
  },
  addManual: (data) => {
    const formData = new FormData();
    Object.entries(data).forEach(([key, value]) => {
      if (value !== null && value !== undefined) {
        formData.append(key, value);
      }
    });
    return api.post('/line-assignments/manual', formData);
  },
  getForTournament: (tournamentId) => api.get(`/line-assignments/tournament/${tournamentId}`),
  getMyAssignments: (familyArcherId) => api.get('/line-assignments/my-assignments', { 
    params: familyArcherId ? { family_archer_id: familyArcherId } : {} 
  }),
  getClubAssignments: () => api.get('/line-assignments/club-assignments'),
  delete: (assignmentId) => api.delete(`/line-assignments/${assignmentId}`),
};

export default api;
